// Advisor-facing career assessment pages (CareerProjectSetup / CareerProjectDetail)
export const careerProjectTranslations = {
  en: {
    setup: {
      title: "New career assessment",
      subtitle: "Set up an assessment and get a private link to send to your candidate.",
      voice: {
        title: "Voice & conversation settings",
        help: "Used in the voice modes the candidate can choose. ElevenLabs voices require the ElevenLabs key on the server.",
        ttsVoice: "AI voice (read-aloud)",
        ttsAuto: "Automatic (ElevenLabs · Bella)",
        realtimeVoice: "Real-time conversation voice",
        realtimeAuto: "Automatic (alloy)",
        asrEngine: "Speech recognition (ASR)",
        maxDuration: "Maximum interview length",
        maxDurationHelp: "The AI wraps up the conversation within this limit.",
      },
      candidateSection: "Candidate",
      candidateName: "Candidate name",
      candidateNameHint: "Optional — leave blank to auto-fill from the candidate's intake form.",
      candidateEmail: "Candidate email",
      targetRole: "Target role / job description",
      targetRolePlaceholder: "Paste the job ad or describe the role the candidate is aiming for…",
      sector: "Sector",
      sectors: {
        ICT: "ICT",
        Finance: "Finance",
        Healthcare: "Healthcare",
        Education: "Education",
        Public_Admin: "Public administration",
        Manufacturing: "Manufacturing",
        Consulting: "Consulting",
        Other: "Other"
      },
      modulesSection: "Assessment modules",
      modulesHelp: "Choose which parts of the assessment the candidate goes through.",
      modules: {
        background_survey: "Background form",
        sjt: "Situational judgement test (SJT)",
        work_aptitude: "Work aptitude",
        jungian: "Personality (Jungian / MBTI)",
        deep_exploration: "AI interview"
      },
      modalitiesSection: "Allowed interview modalities",
      modalitiesHelp: "Which conversation formats the candidate may choose for the AI interview.",
      modalities: {
        REALTIME: "Real-time conversation (live speech)",
        TEXT_TO_TEXT: "Text ↔ Text",
        TEXT_TO_VOICE: "Text → Voice",
        VOICE_TO_TEXT: "Voice → Text",
        VOICE_TO_VOICE: "Voice ↔ Voice"
      },
      moduleConfig: {
        minutesChip: "≈ {{minutes}} min",
        background_survey: {
          description: "A short intake form the candidate completes before the assessment starts.",
          builderHint: "The candidate answers these questions before the first module. Edit the fields below and press “Save Form” to keep your changes in this setup — they are stored when the assessment is saved.",
          disabledInfo: "The background form is switched off — the candidate skips this step.",
          formStored: "Form updated — it is stored when you save the assessment."
        },
        sjt: {
          description: "Realistic work situations that measure judgement and decision-making.",
          numItems: "Number of scenarios",
          numItemsHelp: "≈ 40 seconds per scenario. 12 scenarios give the most reliable score.",
          perCategory: "Scenarios per category",
          totalLabel: "total",
          perCategoryHelp: "Situations are drawn at random from six competency areas (leadership, conflict, collaboration, decisions, ethics, adaptability). ≈ 40 seconds each — 2 per category (12 total) is a good balance."
        },
        work_aptitude: {
          description: "Short cognitive tasks on information processing and logical reasoning.",
          numItems: "Number of tasks",
          numItemsHelp: "≈ 30 seconds per task. The full task set is recommended for a complete picture.",
          allItems: "All ({{max}})"
        },
        jungian: {
          description: "Maps the candidate's personality profile (Jungian type / MBTI).",
          full: "Full version — 42 statements, ≈ 8 min",
          short: "Short version — ≈ 4 min",
          tradeoffNote: "The full version gives the best precision and is recommended when the report will be used in counselling. The short version gives an indicative result only."
        },
        deep_exploration: {
          description: "An AI-led, in-depth conversation about the candidate's career situation.",
          focusDimensions: "Focus areas",
          focusDimensionsHelp: "The areas the AI explores in depth during the interview.",
          dimensions: {
            motivation: "Motivation",
            competencies: "Competencies",
            values: "Values",
            constraints: "Constraints",
            aspirations: "Aspirations"
          },
          maxTurns: "Maximum question rounds",
          maxTurnsHelp: "≈ 1 minute per round. More rounds give a richer interview but take longer.",
          customInstructions: "Extra instructions to the AI (optional)",
          customInstructionsPlaceholder: "E.g. topics that deserve extra attention, or things the AI should avoid asking about…",
          v5Hint: "The conversation runs on the interview engine. The settings below — guide, probing depth, voice and duration — apply to the interview the candidate gets.",
          guideTitle: "Interview guide",
          guideHelp: "The script the AI interviewer follows: themes, example questions and instructions on depth and tone. Edit it freely — it is saved with the assessment, and you can revisit it later from the project's Interview tab.",
          guidePlaceholder: "Write the interview guide here — sections, example questions and instructions to the AI…",
          resetTemplate: "Reset to standard template",
          templateRestored: "Standard template restored",
          useProjectOutline: "Use the interview guide in the AI interview",
          useProjectOutlineHelp: "On: the guide above is injected into the AI interview and steers the conversation. Off: the AI works only from the focus areas and your extra instructions.",
          probingDepth: "Probing depth",
          probingDepthHelp: "How persistently the AI follows up on the candidate's answers.",
          probingDepthOptions: {
            light: "Light",
            moderate: "Moderate",
            deep: "Deep"
          },
          supervisor: "Supervisor AI",
          supervisorHelp: "Quality control that monitors progress and coverage during the interview."
        }
      },
      totalTime: "Estimated time for the candidate: ≈ {{minutes}} min",
      discSection: "DISC profile",
      disc: {
        import: "Import existing DISC profile",
        measure: "Measure DISC in this assessment",
        skip: "Skip DISC"
      },
      settingsSection: "Settings",
      language: "Assessment language",
      languages: {
        nb: "Norwegian",
        en: "English"
      },
      linkExpiry: "Link expiry date (optional)",
      linkExpiryHelp: "The candidate link stops working after this date.",
      submit: "Create assessment",
      creating: "Creating…",
      successTitle: "Assessment created",
      successBody: "Send this private link to the candidate. They don't need an account.",
      candidateLink: "Candidate link",
      copyLink: "Copy link",
      linkCopied: "Link copied to clipboard",
      goToProject: "Open assessment overview",
      createAnother: "Create another",
      editTitle: "Edit career assessment",
      editSubtitle: "Update the assessment. The candidate link stays the same.",
      loadingProject: "Loading assessment…",
      backToAssessment: "Back to assessment",
      save: "Save changes",
      saving: "Saving…",
      cancel: "Cancel",
      errors: {
        nameRequired: "Candidate name is required",
        emailInvalid: "Enter a valid email address",
        modulesRequired: "Enable at least one module",
        modalitiesRequired: "Select at least one interview modality",
        focusDimensionsRequired: "Select at least one focus area for the interview",
        createFailed: "Could not create the assessment. Please try again.",
        loadFailed: "Could not load the assessment",
        saveFailed: "Could not save the changes. Please try again."
      }
    },
    // Advisor-registered Persolog DISC profile (card on the Overview tab)
    disc: {
      title: "DISC profile (Persolog)",
      explainer: "Record the candidate's Persolog result from Karrierehuset's own assessment.",
      register: "Register profile",
      primary: "Primary style",
      secondary: "Secondary style",
      noSecondary: "None",
      styles: {
        D: "Dominance",
        I: "Influence",
        S: "Steadiness",
        C: "Conscientiousness"
      },
      scoresSection: "Intensity scores (0–28, optional)",
      scoresHelp: "Fill in all four values, or leave all four empty.",
      assessedAt: "Assessment date (optional)",
      save: "Save",
      saving: "Saving…",
      cancel: "Cancel",
      saved: "DISC profile saved",
      saveFailed: "Could not save the DISC profile",
      administeredBy: "Administered by Karrierehuset",
      assessedOn: "assessed {{date}}",
      edit: "Edit",
      remove: "Remove",
      removeTitle: "Remove DISC profile?",
      removeBody: "This removes the registered Persolog result from the assessment. You can register it again later.",
      removing: "Removing…",
      removed: "DISC profile removed",
      removeFailed: "Could not remove the DISC profile",
      errors: {
        primaryRequired: "Select a primary style",
        secondarySame: "The secondary style must differ from the primary style",
        scoresIncomplete: "Fill in all four intensity values, or leave all four empty",
        scoresRange: "Intensity values must be between 0 and 28"
      }
    },
    detail: {
      title: "Career assessment",
      tabs: {
        overview: "Overview",
        report: "Report",
        interview: "Interview",
        settings: "Settings"
      },
      reportTab: {
        dims: {
          digital: "Digital & technical literacy",
          analytical: "Analysis & problem-solving",
          communication: "Communication",
          collaboration: "Collaboration & empathy",
          creativity: "Creativity & idea generation",
          execution: "Execution & reliability",
          leadership: "Leadership & influence",
          adaptability: "Adaptability & learning",
        },
        interviewFound: "Interview transcript found ({{turns}} messages) — the report will use the conversation.",
        interviewMissing: "No completed interview yet — the report will be generated without the conversation part.",
        sessionPicker: "Assessment session",
        sessionPickerHelp: "The report is built from one completed session.",
        sessionOption: "{{date}} — {{status}}",
        noCompletedSessions: "The candidate hasn't completed the assessment yet.",
        noReportYet: "No report has been generated for this session yet.",
        generate: "Generate report",
        generatingHint: "This usually takes 30–60 seconds…",
        generateFailed: "Could not generate the report. Please try again.",
        generateConflict: "The report could not be generated — it may already exist, or the session isn't completed. Reloading…",
        generated: "Report generated",
        loadFailed: "Could not load the report",
        retry: "Try again",
        statusDraft: "Draft — for review",
        statusApproved: "Approved {{date}}",
        mbtiChip: "MBTI: {{type}}",
        approve: "Approve report",
        approveConfirmTitle: "Approve report?",
        approveConfirmBody: "By approving, you as the advisor confirm that you have reviewed the report and take responsibility for its content.",
        approveSuccess: "Report approved",
        approveFailed: "Could not approve the report",
        approving: "Approving…",
        download: "Download Word",
        downloadWord: "Download Word",
        downloadPdf: "Download PDF",
        downloading: "Downloading…",
        downloadFailed: "Could not download the report",
        regenerate: "Regenerate",
        regenerateConfirmTitle: "Regenerate report?",
        regenerateConfirmBody: "Regenerating creates a brand-new report and discards all edits you have made. This cannot be undone.",
        edit: "Edit",
        save: "Save",
        cancel: "Cancel",
        saving: "Saving…",
        saved: "Change saved",
        savedBackToDraft: "Change saved — the report has been returned to draft",
        saveFailed: "Could not save the change",
        listEditHelp: "One item per line.",
        readOnlySection: "Structured data — read-only",
        sections: {
          executive_summary: "Executive summary",
          personality: "Personality profile",
          ai_personality: "Personality profile",
          competency_matrix: "Competency matrix",
          strengths: "Strengths",
          ai_strengths: "Strengths",
          ai_talents: "Talents",
          growth: "Development areas",
          development: "Development areas",
          growth_areas: "Development areas",
          development_areas: "Development areas",
          ai_growth: "Development areas",
          recommendations: "Recommendations",
          ai_recommendations: "Recommendations",
          ai_work_fit: "Role fit",
          ai_work_adaptation: "Workplace adaptation",
          ai_conversation_insights: "Insights from the conversation",
          ai_language_signals: "Language signals",
          ai_defense_analysis: "Defence analysis",
          defense_mechanisms: "Defence mechanisms",
          attachment_style: "Attachment style",
          personality_structure: "Personality structure",
          core_conflicts: "Core themes",
          industry_fit: "Industry fit",
          sjt_score: "Situational judgement (SJT)",
          work_aptitude_type: "Work style",
          mbti_type: "Jungian type",
          function_scores: "Cognitive functions",
          stack: "Function stack",
          coaching_guidance: "Coaching notes"
        },
        fields: {
          name: "Name",
          evidence: "Evidence",
          strength: "Severity",
          workplace_cost: "Workplace cost",
          overall_pattern: "Overall pattern",
          primary_defenses: "Primary defences",
          industry: "Industry",
          match_score: "Match",
          role_examples: "Example roles",
          top_gaps: "Largest gaps",
          dimension: "Dimension",
          description: "Description",
          have: "Score",
          need: "Required",
          gap: "Gap"
        },
        sjtOf: "of",
        confidence: "Confidence",
        generateStarted: "Report generation started — this can take a few minutes",
        generatingTitle: "Generating the report…",
        generatingHint: "The AI is writing and reviewing the report. This usually takes 2–5 minutes — you can leave this page and come back.",
        generateTimeout: "Report generation is taking unusually long — check back in a few minutes"
      },
      interviewTab: {
        title: "AI interview for this candidate",
        body: "This assessment includes an AI-led interview. Here you configure how that interview runs for this candidate — the configuration applies to the interview step of the assessment.",
        point1: "Interview guide and topics",
        point2: "Conversation format (text or voice)",
        point3: "Voice and speech settings (TTS/ASR)",
        point4: "Live supervision and transcripts",
        open: "Open interview configuration"
      },
      settingsTab: {
        editTitle: "Assessment settings",
        editBody: "Change candidate details, modules, DISC option, language and link expiry.",
        editButton: "Edit assessment",
        dangerTitle: "Danger zone",
        dangerBody: "Regenerating the candidate link immediately invalidates the old one. A candidate who already received the old link loses access until you send the new link."
      },
      loading: "Loading assessment…",
      notFound: "Assessment not found",
      backToProjects: "Back to projects",
      candidateSection: "Candidate",
      email: "Email",
      targetRole: "Target role",
      sector: "Sector",
      discSource: "DISC",
      language: "Language",
      expires: "Link expires",
      noExpiry: "No expiry",
      linkSection: "Candidate link",
      copyLink: "Copy link",
      linkCopied: "Link copied to clipboard",
      regenerate: "Regenerate link",
      regenerateConfirm: "Regenerating invalidates the old link. The candidate must use the new one. Continue?",
      regenerated: "New link generated",
      regenerateFailed: "Could not regenerate the link",
      sessionsSection: "Sessions",
      noSessions: "No sessions yet — the candidate hasn't opened the link.",
      refresh: "Refresh",
      status: {
        invited: "Invited",
        in_progress: "In progress",
        completed: "Completed",
        expired: "Expired"
      },
      started: "Started",
      updated: "Last activity",
      reportSection: "Report",
      reportNotGenerated: "No report generated yet.",
      generateReport: "Generate report",
      viewReport: "View report",
      generating: "Generating report — this can take a minute…",
      reportFailed: "Could not load the report",
      hideReport: "Hide report",
      sessionsFailed: "Could not load sessions"
    }
  },
  nb: {
    setup: {
      title: "Ny karrierekartlegging",
      subtitle: "Sett opp en kartlegging og få en privat lenke du kan sende til kandidaten.",
      voice: {
        title: "Stemme- og samtaleinnstillinger",
        help: "Brukes i talemodusene kandidaten kan velge. ElevenLabs-stemmer krever ElevenLabs-nøkkel på serveren.",
        ttsVoice: "AI-stemme (opplesning)",
        ttsAuto: "Automatisk (ElevenLabs · Bella)",
        realtimeVoice: "Stemme i sanntidssamtale",
        realtimeAuto: "Automatisk (alloy)",
        asrEngine: "Talegjenkjenning (ASR)",
        maxDuration: "Maksimal intervjulengde",
        maxDurationHelp: "AI-en avrunder samtalen innenfor denne grensen.",
      },
      candidateSection: "Kandidat",
      candidateName: "Kandidatens navn",
      candidateNameHint: "Valgfritt — la stå tomt for å fylles automatisk fra kandidatens skjema.",
      candidateEmail: "Kandidatens e-post",
      targetRole: "Målstilling / stillingsbeskrivelse",
      targetRolePlaceholder: "Lim inn stillingsannonsen eller beskriv stillingen kandidaten sikter mot…",
      sector: "Bransje",
      sectors: {
        ICT: "IKT",
        Finance: "Finans",
        Healthcare: "Helse",
        Education: "Utdanning",
        Public_Admin: "Offentlig forvaltning",
        Manufacturing: "Industri",
        Consulting: "Konsulentvirksomhet",
        Other: "Annet"
      },
      modulesSection: "Kartleggingsmoduler",
      modulesHelp: "Velg hvilke deler av kartleggingen kandidaten skal gjennomføre.",
      modules: {
        background_survey: "Bakgrunnsskjema",
        sjt: "Situasjonsbedømmelsestest (SJT)",
        work_aptitude: "Arbeidsevner",
        jungian: "Personlighet (Jungiansk / MBTI)",
        deep_exploration: "AI-intervju"
      },
      modalitiesSection: "Tillatte intervjuformer",
      modalitiesHelp: "Hvilke samtaleformer kandidaten kan velge for AI-intervjuet.",
      modalities: {
        REALTIME: "Sanntidssamtale (direkte tale)",
        TEXT_TO_TEXT: "Tekst ↔ Tekst",
        TEXT_TO_VOICE: "Tekst → Tale",
        VOICE_TO_TEXT: "Tale → Tekst",
        VOICE_TO_VOICE: "Tale ↔ Tale"
      },
      moduleConfig: {
        minutesChip: "≈ {{minutes}} min",
        background_survey: {
          description: "Et kort skjema kandidaten fyller ut før kartleggingen starter.",
          builderHint: "Kandidaten besvarer disse spørsmålene før første modul. Rediger feltene under og trykk «Lagre Skjema» for å ta endringene med i oppsettet — de lagres når kartleggingen lagres.",
          disabledInfo: "Bakgrunnsskjemaet er slått av — kandidaten hopper over dette steget.",
          formStored: "Skjemaet er oppdatert — det lagres når du lagrer kartleggingen."
        },
        sjt: {
          description: "Realistiske arbeidssituasjoner som måler dømmekraft og beslutningsevne.",
          numItems: "Antall situasjoner",
          numItemsHelp: "≈ 40 sekunder per situasjon. 12 situasjoner gir det mest pålitelige resultatet.",
          perCategory: "Situasjoner per kategori",
          totalLabel: "totalt",
          perCategoryHelp: "Situasjonene trekkes tilfeldig fra seks kompetanseområder (ledelse, konflikt, samarbeid, beslutninger, etikk, omstilling). ≈ 40 sekunder hver — 2 per kategori (12 totalt) gir en god balanse."
        },
        work_aptitude: {
          description: "Korte kognitive oppgaver om informasjonsbehandling og logisk resonnering.",
          numItems: "Antall oppgaver",
          numItemsHelp: "≈ 30 sekunder per oppgave. Hele oppgavesettet anbefales for et fullstendig bilde.",
          allItems: "Alle ({{max}})"
        },
        jungian: {
          description: "Kartlegger kandidatens personlighetsprofil (jungiansk type / MBTI).",
          full: "Full versjon — 42 påstander, ≈ 8 min",
          short: "Kortversjon — ≈ 4 min",
          tradeoffNote: "Fullversjonen gir best presisjon og anbefales når rapporten skal brukes i veiledning. Kortversjonen gir kun et veiledende bilde."
        },
        deep_exploration: {
          description: "En AI-ledet dybdesamtale om kandidatens karrieresituasjon.",
          focusDimensions: "Fokusområder",
          focusDimensionsHelp: "Områdene AI-en går i dybden på under intervjuet.",
          dimensions: {
            motivation: "Motivasjon",
            competencies: "Kompetanse",
            values: "Verdier",
            constraints: "Rammebetingelser",
            aspirations: "Ambisjoner"
          },
          maxTurns: "Maks antall spørsmålsrunder",
          maxTurnsHelp: "≈ 1 minutt per runde. Flere runder gir et rikere intervju, men tar lengre tid.",
          customInstructions: "Egne instruksjoner til AI-en (valgfritt)",
          customInstructionsPlaceholder: "F.eks. temaer som fortjener ekstra oppmerksomhet, eller ting AI-en bør unngå å spørre om…",
          v5Hint: "Samtalen kjøres av intervjumotoren. Innstillingene nedenfor — guide, oppfølgingsdybde, stemme og varighet — gjelder intervjuet kandidaten får.",
          guideTitle: "Intervjuguide",
          guideHelp: "Manuset AI-intervjueren følger: temaer, eksempelspørsmål og instruksjoner om dybde og tone. Rediger den fritt — den lagres sammen med kartleggingen, og du kan komme tilbake til den senere fra fanen «Intervju» på prosjektet.",
          guidePlaceholder: "Skriv intervjuguiden her — temaer, eksempelspørsmål og instruksjoner til AI-en…",
          resetTemplate: "Tilbakestill til standardmal",
          templateRestored: "Standardmalen er satt inn",
          useProjectOutline: "Bruk intervjuguiden i AI-intervjuet",
          useProjectOutlineHelp: "På: guiden over legges inn i AI-intervjuet og styrer samtalen. Av: AI-en jobber kun ut fra fokusområdene og egne instruksjoner.",
          probingDepth: "Oppfølgingsdybde",
          probingDepthHelp: "Hvor langt AI-en går i å følge opp kandidatens svar.",
          probingDepthOptions: {
            light: "Lett",
            moderate: "Moderat",
            deep: "Dyp"
          },
          supervisor: "Veileder-AI",
          supervisorHelp: "Kvalitetskontroll som overvåker fremdrift og dekning under intervjuet."
        }
      },
      totalTime: "Anslått tid for kandidaten: ≈ {{minutes}} min",
      discSection: "DISC-profil",
      disc: {
        import: "Importer eksisterende DISC-profil",
        measure: "Mål DISC i denne kartleggingen",
        skip: "Hopp over DISC"
      },
      settingsSection: "Innstillinger",
      language: "Kartleggingsspråk",
      languages: {
        nb: "Norsk",
        en: "Engelsk"
      },
      linkExpiry: "Utløpsdato for lenken (valgfritt)",
      linkExpiryHelp: "Kandidatlenken slutter å virke etter denne datoen.",
      submit: "Opprett kartlegging",
      creating: "Oppretter…",
      successTitle: "Kartlegging opprettet",
      successBody: "Send denne private lenken til kandidaten. De trenger ikke konto.",
      candidateLink: "Kandidatlenke",
      copyLink: "Kopier lenke",
      linkCopied: "Lenken er kopiert til utklippstavlen",
      goToProject: "Åpne oversikten",
      createAnother: "Opprett en til",
      editTitle: "Rediger karrierekartlegging",
      editSubtitle: "Oppdater kartleggingen. Kandidatlenken forblir den samme.",
      loadingProject: "Laster kartlegging…",
      backToAssessment: "Tilbake til kartleggingen",
      save: "Lagre endringer",
      saving: "Lagrer…",
      cancel: "Avbryt",
      errors: {
        nameRequired: "Kandidatens navn er påkrevd",
        emailInvalid: "Oppgi en gyldig e-postadresse",
        modulesRequired: "Aktiver minst én modul",
        modalitiesRequired: "Velg minst én intervjuform",
        focusDimensionsRequired: "Velg minst ett fokusområde for intervjuet",
        createFailed: "Kunne ikke opprette kartleggingen. Prøv igjen.",
        loadFailed: "Kunne ikke laste kartleggingen",
        saveFailed: "Kunne ikke lagre endringene. Prøv igjen."
      }
    },
    // Veilederregistrert Persolog DISC-profil (kort på Oversikt-fanen)
    disc: {
      title: "DISC-profil (Persolog)",
      explainer: "Registrer kandidatens Persolog-resultat fra Karrierehusets egen kartlegging.",
      register: "Registrer profil",
      primary: "Primær stil",
      secondary: "Sekundær stil",
      noSecondary: "Ingen",
      styles: {
        D: "Dominans",
        I: "Innflytelse",
        S: "Stabilitet",
        C: "Samvittighetsfullhet"
      },
      scoresSection: "Intensitetsverdier (0–28, valgfritt)",
      scoresHelp: "Fyll ut alle fire verdiene, eller la alle stå tomme.",
      assessedAt: "Kartleggingsdato (valgfritt)",
      save: "Lagre",
      saving: "Lagrer…",
      cancel: "Avbryt",
      saved: "DISC-profilen er lagret",
      saveFailed: "Kunne ikke lagre DISC-profilen",
      administeredBy: "Administrert av Karrierehuset",
      assessedOn: "kartlagt {{date}}",
      edit: "Rediger",
      remove: "Fjern",
      removeTitle: "Fjerne DISC-profilen?",
      removeBody: "Dette fjerner det registrerte Persolog-resultatet fra kartleggingen. Du kan registrere det på nytt senere.",
      removing: "Fjerner…",
      removed: "DISC-profilen er fjernet",
      removeFailed: "Kunne ikke fjerne DISC-profilen",
      errors: {
        primaryRequired: "Velg primær stil",
        secondarySame: "Sekundær stil må være forskjellig fra primær stil",
        scoresIncomplete: "Fyll ut alle fire intensitetsverdiene, eller la alle stå tomme",
        scoresRange: "Intensitetsverdiene må være mellom 0 og 28"
      }
    },
    detail: {
      title: "Karrierekartlegging",
      tabs: {
        overview: "Oversikt",
        report: "Rapport",
        interview: "Intervju",
        settings: "Innstillinger"
      },
      reportTab: {
        dims: {
          digital: "Digital og teknisk kompetanse",
          analytical: "Analyse og problemløsning",
          communication: "Kommunikasjon",
          collaboration: "Samarbeid og empati",
          creativity: "Kreativitet og idéutvikling",
          execution: "Gjennomføring og pålitelighet",
          leadership: "Ledelse og innflytelse",
          adaptability: "Omstillingsevne og læring",
        },
        interviewFound: "Intervjutranskript funnet ({{turns}} meldinger) — rapporten bruker samtalen.",
        interviewMissing: "Ingen fullført intervju ennå — rapporten genereres uten samtaledelen.",
        sessionPicker: "Kartleggingsøkt",
        sessionPickerHelp: "Rapporten bygges på én fullført økt.",
        sessionOption: "{{date}} — {{status}}",
        noCompletedSessions: "Kandidaten har ikke fullført kartleggingen ennå.",
        noReportYet: "Ingen rapport er generert for denne økten ennå.",
        generate: "Generer rapport",
        generatingHint: "Dette tar gjerne 30–60 sekunder…",
        generateFailed: "Kunne ikke generere rapporten. Prøv igjen.",
        generateConflict: "Rapporten kunne ikke genereres — den finnes kanskje allerede, eller økten er ikke fullført. Laster på nytt…",
        generated: "Rapporten er generert",
        loadFailed: "Kunne ikke laste rapporten",
        retry: "Prøv igjen",
        statusDraft: "Utkast — til gjennomgang",
        statusApproved: "Godkjent {{date}}",
        mbtiChip: "MBTI: {{type}}",
        approve: "Godkjenn rapport",
        approveConfirmTitle: "Godkjenn rapporten?",
        approveConfirmBody: "Ved å godkjenne bekrefter du som veileder at du har gjennomgått rapporten og tar ansvar for innholdet.",
        approveSuccess: "Rapporten er godkjent",
        approveFailed: "Kunne ikke godkjenne rapporten",
        approving: "Godkjenner…",
        download: "Last ned Word",
        downloadWord: "Last ned Word",
        downloadPdf: "Last ned PDF",
        downloading: "Laster ned…",
        downloadFailed: "Kunne ikke laste ned rapporten",
        regenerate: "Regenerer",
        regenerateConfirmTitle: "Regenerer rapporten?",
        regenerateConfirmBody: "Å regenerere lager en helt ny rapport og forkaster alle endringene du har gjort. Dette kan ikke angres.",
        edit: "Rediger",
        save: "Lagre",
        cancel: "Avbryt",
        saving: "Lagrer…",
        saved: "Endring lagret",
        savedBackToDraft: "Endring lagret — rapporten er satt tilbake til utkast",
        saveFailed: "Kunne ikke lagre endringen",
        listEditHelp: "Ett punkt per linje.",
        readOnlySection: "Strukturerte data — skrivebeskyttet",
        sections: {
          executive_summary: "Sammendrag",
          personality: "Personlighetsprofil",
          ai_personality: "Personlighetsprofil",
          competency_matrix: "Kompetansematrise",
          strengths: "Styrker",
          ai_strengths: "Styrker",
          ai_talents: "Talenter",
          growth: "Utviklingsområder",
          development: "Utviklingsområder",
          growth_areas: "Utviklingsområder",
          development_areas: "Utviklingsområder",
          ai_growth: "Utviklingsområder",
          recommendations: "Anbefalinger",
          ai_recommendations: "Anbefalinger",
          ai_work_fit: "Rollematch",
          ai_work_adaptation: "Tilpasning i arbeidslivet",
          ai_conversation_insights: "Innsikter fra samtalen",
          ai_language_signals: "Språklige signaler",
          ai_defense_analysis: "Forsvarsanalyse",
          defense_mechanisms: "Forsvarsmekanismer",
          attachment_style: "Tilknytningsstil",
          personality_structure: "Personlighetsstruktur",
          core_conflicts: "Kjernetemaer",
          industry_fit: "Bransjetilpasning",
          sjt_score: "Situasjonsbedømmelse (SJT)",
          work_aptitude_type: "Arbeidsstil",
          mbti_type: "Jung-type",
          function_scores: "Kognitive funksjoner",
          stack: "Funksjonsstakk",
          coaching_guidance: "Veiledningsnotater"
        },
        fields: {
          name: "Navn",
          evidence: "Belegg",
          strength: "Alvorlighetsgrad",
          workplace_cost: "Kostnad i arbeidslivet",
          overall_pattern: "Helhetsmønster",
          primary_defenses: "Primære forsvar",
          industry: "Bransje",
          match_score: "Match",
          role_examples: "Eksempelroller",
          top_gaps: "Største gap",
          dimension: "Dimensjon",
          description: "Beskrivelse",
          have: "Skår",
          need: "Krav",
          gap: "Gap"
        },
        sjtOf: "av",
        confidence: "Sikkerhet",
        generateStarted: "Rapportgenerering startet — dette kan ta noen minutter",
        generatingTitle: "Genererer rapporten…",
        generatingHint: "AI-en skriver og kvalitetssikrer rapporten. Dette tar vanligvis 2–5 minutter — du kan forlate siden og komme tilbake.",
        generateTimeout: "Rapportgenereringen tar uvanlig lang tid — sjekk igjen om noen minutter"
      },
      interviewTab: {
        title: "AI-intervju for denne kandidaten",
        body: "Kartleggingen inkluderer et AI-ledet intervju. Her setter du opp hvordan intervjuet gjennomføres for denne kandidaten — oppsettet gjelder intervjudelen av kartleggingen.",
        point1: "Intervjuguide og temaer",
        point2: "Samtaleform (tekst eller tale)",
        point3: "Tale- og stemmeinnstillinger (TTS/ASR)",
        point4: "Sanntidsovervåking og transkripsjoner",
        open: "Åpne intervjuoppsettet"
      },
      settingsTab: {
        editTitle: "Innstillinger for kartleggingen",
        editBody: "Endre kandidatopplysninger, moduler, DISC-valg, språk og utløpsdato for lenken.",
        editButton: "Rediger kartleggingen",
        dangerTitle: "Faresone",
        dangerBody: "Å lage ny kandidatlenke gjør den gamle ugyldig umiddelbart. En kandidat som allerede har fått den gamle lenken mister tilgang til du sender den nye."
      },
      loading: "Laster kartlegging…",
      notFound: "Fant ikke kartleggingen",
      backToProjects: "Tilbake til prosjekter",
      candidateSection: "Kandidat",
      email: "E-post",
      targetRole: "Målstilling",
      sector: "Bransje",
      discSource: "DISC",
      language: "Språk",
      expires: "Lenken utløper",
      noExpiry: "Ingen utløpsdato",
      linkSection: "Kandidatlenke",
      copyLink: "Kopier lenke",
      linkCopied: "Lenken er kopiert til utklippstavlen",
      regenerate: "Lag ny lenke",
      regenerateConfirm: "Å lage ny lenke gjør den gamle ugyldig. Kandidaten må bruke den nye. Fortsette?",
      regenerated: "Ny lenke opprettet",
      regenerateFailed: "Kunne ikke lage ny lenke",
      sessionsSection: "Økter",
      noSessions: "Ingen økter ennå — kandidaten har ikke åpnet lenken.",
      refresh: "Oppdater",
      status: {
        invited: "Invitert",
        in_progress: "Pågår",
        completed: "Fullført",
        expired: "Utløpt"
      },
      started: "Startet",
      updated: "Siste aktivitet",
      reportSection: "Rapport",
      reportNotGenerated: "Ingen rapport generert ennå.",
      generateReport: "Generer rapport",
      viewReport: "Vis rapport",
      generating: "Genererer rapport — dette kan ta et minutt…",
      reportFailed: "Kunne ikke laste rapporten",
      hideReport: "Skjul rapport",
      sessionsFailed: "Kunne ikke laste øktene"
    }
  }
};
